import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { queryKeys, queryUtils, useDeletePdpMutation } from '@vizit/api';
import { type ImageType, PdpStatus } from '@vizit/api';
import {
  AssetGridViewer,
  AssetTableFilterOptions,
  AssetTableFilters,
  AssetTableProvider,
  AssetUploadDialog,
} from '@vizit/assets';
import { AlertLevel, Separator, Tab, TabList, TabPanel, TabSize, TabVariant, Tabs, useToast } from '@vizit/atoms';
import {
  CollectionGridProvider,
  CollectionGridViewer,
  CollectionNewDialog,
  useCollectionMenuActions,
} from '@vizit/collections';
import type { RetailerKey } from '@vizit/constants';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { RefreshCw04Icon, Trash01Icon } from '@vizit/icons';
import type { ProductListingData, RowAction } from '@vizit/molecules';
import type { ScoreLevel } from '@vizit/organisms';
import { DeleteProductDialog, RefreshProductDialog, isRefreshAllowed } from '@vizit/products';
import {
  ProductBulkImportDialog,
  ProductImportDialog,
  ProductNewDialog,
  ProductTableFilterOptions,
  ProductTableFilters,
  ProductTableProvider,
  ProductTableViewerVirtualized,
} from '@vizit/products';
import { cx } from '@vizit/theme';
import * as React from 'react';
import type { PressEvent } from 'react-aria';
import { getLibraryBreadcrumb, navigateWithState } from '../../../router/route-utils';
import { usePage } from '../../providers';

enum LibraryTabOptions {
  Assets = 'assets',
  Products = 'products',
  Collections = 'collections',
}

interface LibraryPageProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional CSS classes to apply to the component.
   *
   * @optional
   * @type string
   */
  className?: string;
  /**
   * The currently selected tab
   * @required
   * @type {LibraryTabOptions}
   */
  selectedTab: LibraryTabOptions;
  /**
   * The ID of the portfolio to fetch assets for
   * @optional
   * @type {string}
   */
  portfolioId?: string;
  /**
   * The product table filters
   * @optional
   * @type {Record<ProductTableFilterOptions, string>}
   */
  productTableFilters?: Record<ProductTableFilterOptions, string>;
  /**
   * The asset table filters
   * @optional
   * @type {Record<AssetTableFilterOptions, string>}
   */
  assetTableFilters?: Record<AssetTableFilterOptions, string>;
  /**
   * The ID of the organization to use for fetching images
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * The function to call when the tab changes
   * @required
   * @type {(key: LibraryTabOptions) => Promise<void>}
   * @param key - The key of the tab to change to
   */
  onTabChange: (key: LibraryTabOptions) => Promise<void>;
  /**
   * The function to call when the product table filters change
   * @optional
   * @type {(filter: ProductTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onProductTableFilterChange?: (filter: ProductTableFilterOptions, value?: string) => void;
  /**
   * The function to call when the asset table filters change
   * @optional
   * @type {(filter: AssetTableFilterOptions, value: string) => void}
   * @param filter - The filter option to change
   * @param value - The new value for the filter
   */
  onAssetTableFilterChange?: (filter: AssetTableFilterOptions, value: string) => void;
  /**
   * The function to call when the image is uploaded
   *
   * @param id - The ID of the image
   */
  onSuccessfulUpload: (id: string) => void;
  /**
   * The function to call when a PDP is successfully submitted
   *
   * @param pdpId - The ID of the PDP
   */
  onSuccessfulImport: (pdpId: string) => void;
}

/**
 * Main library page that displays the user's asset library.
 * This page will contain all uploaded assets and products.
 *
 * @param props - The component props
 * @param props.className - Additional CSS classes to apply
 * @param props.selectedTab - The currently selected tab
 * @param props.portfolioId - The ID of the portfolio to fetch assets for
 * @param props.productTableFilters - The product table filters
 * @param props.assetTableFilters - The asset table filters
 * @param props.organizationId - The ID of the organization to use for fetching images
 * @param props.onTabChange - The function to call when the tab changes
 * @param props.onProductTableFilterChange - The function to call when product table filters change
 * @param props.onAssetTableFilterChange - The function to call when asset table filters change
 * @param props.onSuccessfulUpload - The function to call when an image is uploaded
 * @param props.onSuccessfulImport - The function to call when a PDP is successfully imported
 *
 * @example
 * <LibraryPage selectedTab={LibraryTabOptions.Assets} portfolioId="123" organizationId="org-123" onTabChange={() => {}} onSuccessfulUpload={() => {}} onSuccessfulImport={() => {}} />
 */
function LibraryPage(props: LibraryPageProps) {
  const {
    className,
    selectedTab,
    portfolioId,
    productTableFilters = {},
    assetTableFilters = {},
    organizationId,
    onTabChange,
    onSuccessfulUpload,
    onSuccessfulImport,
    onProductTableFilterChange,
    onAssetTableFilterChange,
    ...rest
  } = props;

  const router = useRouter();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { onAuthenticationError } = usePage();

  const [actionModal, setActionModal] = React.useState<React.ReactNode>(null);

  const closeActionModal = React.useCallback(() => {
    setActionModal((prev) => {
      if (prev && React.isValidElement(prev)) {
        return React.cloneElement(prev as React.ReactElement<{ isOpen: boolean }>, { isOpen: false });
      }
      return prev;
    });
    setTimeout(() => setActionModal(null), 300);
  }, []);

  const deletePdpMutation = useDeletePdpMutation({
    onSuccess: async () => {
      await queryUtils.invalidateAllPdpLists(queryClient);
      closeActionModal();
    },
    onError: (error) => {
      addToast({
        title: 'Failed to delete product',
        description: 'An error occurred while deleting the product',
        level: AlertLevel.Error,
      });
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Product,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productDelete,
        },
      });
    },
  });

  const handleDeleteProduct = React.useCallback(
    async (pdpId: string) => {
      if (!portfolioId) {
        addToast({
          title: 'Failed to delete product page',
          description: 'Portfolio ID is required',
          level: AlertLevel.Error,
        });

        Sentry.captureException(new Error('Portfolio ID is required'), {
          level: 'error',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Product,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].productDelete,
          },
        });
        return;
      }

      await deletePdpMutation.mutateAsync({
        portfolio_id: portfolioId,
        pdp_id: pdpId,
      });
    },
    [portfolioId, deletePdpMutation, addToast],
  );

  const handleDeleteClick = React.useCallback(
    (item: ProductListingData) => {
      const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
          closeActionModal();
        }
      };

      setActionModal(
        <DeleteProductDialog
          isOpen={true}
          onOpenChange={handleOpenChange}
          productId={item.id}
          onDelete={handleDeleteProduct}
        />,
      );
    },
    [handleDeleteProduct, closeActionModal],
  );

  const handleRefreshPdp = React.useCallback(
    (item: ProductListingData) => {
      if (!portfolioId) return;

      const handleOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
          closeActionModal();
        }
      };

      setActionModal(
        <RefreshProductDialog
          portfolioId={portfolioId}
          productId={item.id}
          isOpen={true}
          onOpenChange={handleOpenChange}
        />,
      );
    },
    [portfolioId, closeActionModal],
  );

  const rowActions = React.useMemo<RowAction[]>(
    () =>
      [
        {
          id: 'refresh',
          icon: <RefreshCw04Icon size={20} className="text-black" />,
          label: 'Refresh PDP for',
          onAction: handleRefreshPdp,
          tooltip: (item: ProductListingData) =>
            item.status === PdpStatus.ScrapeStarted ? 'refreshing...' : 'Refresh page data',
          isDisabled: (item: ProductListingData) => !isRefreshAllowed(item),
          isHidden: (item: ProductListingData) => item.sourceType === 'manual',
        },
        {
          id: 'delete',
          icon: <Trash01Icon size={20} />,
          label: 'Delete',
          onAction: handleDeleteClick,
          tooltip: 'Delete page',
        },
      ].filter(Boolean) as RowAction[],
    [handleDeleteClick, handleRefreshPdp],
  );

  const handleNavigateToAsset = React.useCallback(
    (id: string) => {
      navigateWithState(router, {
        to: '/library/assets/$id',
        params: { id },
        from: { trail: [{ ...getLibraryBreadcrumb('assets'), search: { view: selectedTab, ...assetTableFilters } }] },
      });
    },
    [router, selectedTab, assetTableFilters],
  );

  const handleNavigateToProduct = React.useCallback(
    (pdpId: string) => {
      navigateWithState(router, {
        to: '/library/products/$pdpId',
        params: { pdpId },
        search: { products: 'open' },
        from: {
          trail: [{ ...getLibraryBreadcrumb('products'), search: { view: selectedTab, ...productTableFilters } }],
        },
      });
    },
    [router, selectedTab, productTableFilters],
  );

  const handleNavigateToCollection = React.useCallback(
    (collectionId: string, _event: PressEvent) => {
      navigateWithState(router, {
        to: '/library/collections/$collectionId',
        params: { collectionId },
        from: { trail: [{ ...getLibraryBreadcrumb('collections'), search: { view: selectedTab } }] },
      });
    },
    [router, selectedTab],
  );

  const handleTabChange = React.useCallback(
    async (key: React.Key | null) => {
      const tab = key as LibraryTabOptions;
      await onTabChange(tab);
    },
    [onTabChange],
  );

  // Context: https://github.com/AdharkInc/vizit-v2-ui/pull/120#discussion_r2210748466
  // UI for Asset Grid Viewer panel is flickering skeletons on switching tabs
  const assetsPanel = React.useMemo(
    () => (
      <TabPanel id="assets" className="flex flex-col items-center flex-1 w-full mt-0">
        <div className="w-full max-w-screen-4xl mx-auto pt-12 px-8 max-sm:pt-8 max-sm:px-4">
          <AssetTableProvider
            portfolioId={portfolioId ?? undefined}
            productCategoryId={
              assetTableFilters && AssetTableFilterOptions.ProductCategory in assetTableFilters
                ? (assetTableFilters[AssetTableFilterOptions.ProductCategory] as string)
                : undefined
            }
            productCategoryName={
              assetTableFilters && AssetTableFilterOptions.ProductCategoryName in assetTableFilters
                ? (assetTableFilters[AssetTableFilterOptions.ProductCategoryName] as string)
                : undefined
            }
            imageType={
              assetTableFilters && AssetTableFilterOptions.ImageType in assetTableFilters
                ? (assetTableFilters[AssetTableFilterOptions.ImageType] as ImageType)
                : undefined
            }
            onAssetTableFilterChange={onAssetTableFilterChange}
          >
            <AssetTableFilters
              organizationId={organizationId ?? undefined}
              onAuthenticationError={onAuthenticationError}
            />
            <div className="w-full max-h-[calc(100vh-150px)] overflow-y-auto">
              <AssetGridViewer
                uploadAssetDialog={
                  <AssetUploadDialog
                    organizationId={organizationId ?? undefined}
                    portfolioId={portfolioId ?? undefined}
                    onSuccessfulUpload={onSuccessfulUpload}
                    onAuthenticationError={onAuthenticationError}
                  />
                }
                onNavigateToAsset={handleNavigateToAsset}
                className="w-full"
              />
            </div>
          </AssetTableProvider>
        </div>
      </TabPanel>
    ),
    [
      portfolioId,
      organizationId,
      assetTableFilters,
      handleNavigateToAsset,
      onSuccessfulUpload,
      onAuthenticationError,
      onAssetTableFilterChange,
    ],
  );

  const productsPanel = React.useMemo(
    () => (
      <TabPanel id="products" className="flex flex-col items-center flex-1 w-full mt-0">
        <div className="max-h-screen overflow-auto w-full max-w-screen-4xl mx-auto pb-65 pt-12 px-8 max-sm:pt-8 max-sm:px-4">
          <ProductTableProvider
            organizationId={organizationId ?? ''}
            portfolioId={portfolioId ?? undefined}
            brand={
              productTableFilters && ProductTableFilterOptions.Brand in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.Brand] as string)
                : undefined
            }
            listingScoreLevel={
              productTableFilters && ProductTableFilterOptions.ListingScoreLevel in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.ListingScoreLevel] as ScoreLevel)
                : undefined
            }
            productCategoryId={
              productTableFilters && ProductTableFilterOptions.ProductCategory in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.ProductCategory] as string)
                : undefined
            }
            productCategoryName={
              productTableFilters && ProductTableFilterOptions.ProductCategoryName in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.ProductCategoryName] as string)
                : undefined
            }
            searchTerm={
              productTableFilters && ProductTableFilterOptions.Search in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.Search] as string)
                : undefined
            }
            retailer={
              productTableFilters && ProductTableFilterOptions.Retailer in productTableFilters
                ? (productTableFilters[ProductTableFilterOptions.Retailer] as RetailerKey)
                : undefined
            }
            onProductTableFilterChange={onProductTableFilterChange}
          >
            <ProductTableFilters onAuthenticationError={onAuthenticationError} />
            <ProductTableViewerVirtualized
              portfolioId={portfolioId}
              className="max-h-screen w-full"
              onRowClick={handleNavigateToProduct}
              rowActions={rowActions}
              importProductDialog={
                <ProductImportDialog
                  portfolioId={portfolioId ?? undefined}
                  onSuccessfulImport={onSuccessfulImport}
                  onImportError={onAuthenticationError}
                  hideTrigger={true}
                />
              }
              createProductDialog={
                <ProductNewDialog
                  portfolioId={portfolioId ?? undefined}
                  organizationId={organizationId ?? undefined}
                  onSuccessfulCreate={onSuccessfulImport}
                  onAuthenticationError={onAuthenticationError}
                  hideTrigger={true}
                />
              }
              bulkImportDialog={<ProductBulkImportDialog hideTrigger={true} portfolioId={portfolioId ?? undefined} />}
            />
          </ProductTableProvider>
        </div>
      </TabPanel>
    ),
    [
      portfolioId,
      organizationId,
      handleNavigateToProduct,
      rowActions,
      onSuccessfulImport,
      onAuthenticationError,
      onProductTableFilterChange,
      productTableFilters,
    ],
  );

  const [isCollectionDialogOpen, setIsCollectionDialogOpen] = React.useState(false);

  const handleOnSuccessfulCollectionCreate = React.useCallback(
    (collectionId: string) => {
      Sentry.addBreadcrumb({
        message: 'Collection created successfully, invalidating SearchCollectionsInfinite query cache.',
        level: 'info',
        data: { collection_id: collectionId, portfolio_id: portfolioId },
      });

      // Mark the collections query as stale without blocking navigation
      void queryClient.invalidateQueries({
        queryKey: queryKeys.collections.infinite({
          portfolio_id: portfolioId ?? '',
          sort_by: 'created_at',
          limit: 12,
          sort_direction: 'desc',
        }).queryKey,
        refetchType: 'none',
      });

      setIsCollectionDialogOpen(false);

      navigateWithState(router, {
        to: '/library/collections/$collectionId',
        params: { collectionId },
        from: { trail: [{ ...getLibraryBreadcrumb('collections'), search: { view: selectedTab } }] },
      });
    },
    [portfolioId, queryClient, router, selectedTab],
  );

  const { handleRename: handleCollectionRename, handleDelete: handleCollectionDelete } = useCollectionMenuActions({
    portfolioId,
  });

  const collectionsPanel = React.useMemo(
    () => (
      <TabPanel id="collections" className="flex flex-col items-center flex-1 w-full mt-0">
        <div className="w-full max-w-screen-4xl mx-auto pt-12 px-8 max-sm:pt-8 max-sm:px-4">
          <CollectionGridProvider portfolioId={portfolioId ?? undefined} organizationId={organizationId ?? undefined}>
            <div className="w-full max-h-[calc(100vh-100px)] overflow-y-auto pb-32">
              <CollectionGridViewer
                onNavigateToCollection={handleNavigateToCollection}
                className="w-full"
                onCollectionRename={handleCollectionRename}
                onCollectionDelete={handleCollectionDelete}
                onAuthenticationError={onAuthenticationError}
                createCollectionDialog={
                  <CollectionNewDialog
                    portfolioId={portfolioId ?? undefined}
                    organizationId={organizationId ?? undefined}
                    isOpen={isCollectionDialogOpen}
                    onOpenChange={setIsCollectionDialogOpen}
                    onSuccessfulCreate={handleOnSuccessfulCollectionCreate}
                    onAuthenticationError={onAuthenticationError}
                    useIcon={false}
                  />
                }
                headerAction={
                  <CollectionNewDialog
                    portfolioId={portfolioId ?? undefined}
                    organizationId={organizationId ?? undefined}
                    isOpen={isCollectionDialogOpen}
                    onOpenChange={setIsCollectionDialogOpen}
                    onSuccessfulCreate={handleOnSuccessfulCollectionCreate}
                    onAuthenticationError={onAuthenticationError}
                    useIcon={false}
                  />
                }
              />
            </div>
          </CollectionGridProvider>
        </div>
      </TabPanel>
    ),
    [
      portfolioId,
      organizationId,
      isCollectionDialogOpen,
      handleNavigateToCollection,
      handleOnSuccessfulCollectionCreate,
      handleCollectionRename,
      handleCollectionDelete,
      onAuthenticationError,
    ],
  );

  return (
    <>
      <div className={cx('flex flex-col w-full h-full', className)} {...rest}>
        <Tabs
          variant={TabVariant.Underline}
          selectedKey={selectedTab}
          size={TabSize.Md}
          onSelectionChange={handleTabChange}
          className="flex flex-col w-full h-full"
        >
          <TabList className="w-fit px-12 max-sm:px-8 gap-6" aria-label="Library tabs">
            <Tab id="products" data-testid="tab-products">
              Product Pages
            </Tab>
            <Tab id="collections" data-testid="tab-collections">
              Collections
            </Tab>
            <Tab id="assets" data-testid="tab-assets">
              Assets
            </Tab>
          </TabList>
          <Separator />
          {assetsPanel}
          {collectionsPanel}
          {productsPanel}
        </Tabs>
      </div>
      {actionModal}
    </>
  );
}

export { LibraryPage, LibraryTabOptions };
